import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TableProjectionComponent } from "@app/shared/components/table-projection/table-projection.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { PdfService } from "@app/shared/services/pdf.service";
import { ReportBeachmarkingSectionComponent } from "@app/views/auth/report/components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportHeaderComponent } from "@app/views/auth/report/components/report-header/report-header.component";
import { ReportPdfFooterComponent } from "@app/views/auth/report/components/report-pdf-footer/report-pdf-footer.component";
import { ReportPdfIndexComponent } from "@app/views/auth/report/components/report-pdf-index/report-pdf-index.component";
import { ReportPerformanceComponent } from "@app/views/auth/report/components/report-performance/report-performance.component";
import { ReportToolbarComponent } from "@app/views/auth/report/components/report-toolbar/report-toolbar.component";
import { ReportPreferenceService } from "@arch/domain/application/services";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import {
    GetCompanyController,
    GetReportController,
    GetScenariosController,
    GetSeriesAccountsPlanController,
    PopulateReportController
} from "@arch/presentation/controllers";

@Component({
    selector: "app-pdf-budget",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ReportHeaderComponent,
        ReportBeachmarkingSectionComponent,
        ReportToolbarComponent,
        ReportPdfFooterComponent,
        ReportPdfIndexComponent,
        ReportPerformanceComponent,
        TableProjectionComponent
    ],
    templateUrl: "./pdf.component.html",
    styles: [
        `
            .divider {
                border-bottom: var(--border-width-1) solid
                    var(--color-border-light);
            }
            .note-container {
                padding: var(--spacing-8);
                background-color: var(--color-expand-collapse-notes-bg);
                border-radius: var(--border-radius-8);
                gap: var(--spacing-8);
                .fa-sticky-note {
                    font-size: var(--font-size-12);
                }
            }
            .pdf-section-about-visualization {
                margin-top: var(--spacing-8);
            }
        `
    ],
    styleUrls: ["../../pdf-config.scss"]
})
export class PdfProjectionComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly getSeriesAccountsPlanController = inject(
        GetSeriesAccountsPlanController
    );
    private readonly populateReportController = inject(
        PopulateReportController
    );
    public pdfService = inject(PdfService);
    public reportPreferenceService = inject(ReportPreferenceService);
    public report: ReportEntity;
    public company: CompanyEntity;
    public loading: boolean = true;
    public hasBenchmarking: boolean = true;
    public tableBudgetPages: {
        dates?: Date[];
        indexInterval: number[];
    }[] = [];

    async ngOnInit(): Promise<void> {
        this.loading = true;
        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        this.reportPreferenceService.setAll(report.props?.optionsContent);
        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: true
                }
            })
        ]);
        report.setScenarioOptions(scenarios);
        this.report = report;
        this.company = company;
        this.pdfService.config(this.report);
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                accountsPlan: { origin: "accountsPlan" }
            }
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.setTableProjectionPages();
        this.loading = false;
        this.pdfService.print();
    }

    private setTableProjectionPages(): void {
        const projectionSeriesData = this.getProjectionSeries();
        const projectionIndexes = projectionSeriesData.map(
            (data, index) => index
        );
        projectionIndexes.push(projectionSeriesData.length);
        const seriesDataChunks = this.chunkSeriesData(
            projectionIndexes,
            !this.reportPreferenceService?.viewBench ? 34 : 34
        );
        for (const dates of this.pdfService.tableValuesDates) {
            for (const seriesData of seriesDataChunks) {
                this.tableBudgetPages.push({
                    dates,
                    indexInterval: seriesData
                });
            }
        }
    }

    private getProjectionSeries() {
        return this.getSeriesAccountsPlanController.execute(
            this.report.options?.source,
            this.company.props._id
        );
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
    }

    private chunkSeriesData(
        seriesData: number[],
        chunkSize: number
    ): number[][] {
        if (!seriesData || !Array.isArray(seriesData)) return [];
        const seriesDataChunks: number[][] = [];
        let index = 0;
        if (!this.reportPreferenceService?.viewBench) {
            chunkSize = chunkSize - 8;
        }
        for (let i = 0; i < seriesData.length; i += chunkSize) {
            index++;

            if (this.reportPreferenceService?.viewBench) {
                seriesDataChunks.push(seriesData.slice(i, i + chunkSize));
            } else {
                if (index === 1) {
                    seriesDataChunks.push(seriesData.slice(i, chunkSize));
                } else {
                    seriesDataChunks.push(
                        seriesData.slice(i, i + chunkSize + 6)
                    );
                }
            }
        }
        return seriesDataChunks;
    }
}
